import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, Phone } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLoading } from "@/lib/loading";
import { useToast } from "@/lib/toast";
import { GOOGLE_AUTH_URL } from "@/config";
import { Button, Input } from "@/components/app/Primitives";
import { PhoneInput } from "@/components/app/PhoneInput";
import { AuthBackground, GoogleIcon } from "@/components/app/Layout";

export default function Login() {
  const { signIn } = useAuth();
  const { runWithLoading } = useLoading();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("email");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    const em = params.get("email");
    if (em) { setIdentifier(em); setMode("email"); }
  }, [params]);

  const handleSubmit = async () => {
    if (!identifier.trim() || !password) {
      toast.show(mode === "email" ? "Enter email and password" : "Enter mobile and password", "error");
      return;
    }
    setLoading(true);
    try {
      const raw = identifier.trim();
      await runWithLoading(() => signIn({ identifier: mode === "email" ? raw.toLowerCase() : raw, password }), "Signing you in…");
      toast.show("Welcome back!", "success");
      navigate("/", { replace: true });
    } catch (e) {
      toast.show(e instanceof Error ? e.message : "Login failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    const redirect = window.location.origin + "/";
    window.location.href = `${GOOGLE_AUTH_URL}?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <AuthBackground>
      <div className="mb-6 flex flex-col items-center gap-2">
        <img src="/branding/assignoo-display.png" alt="Assignoo" className="h-[200px] w-[320px] object-contain" data-testid="login-brand-logo" />
        <p className="text-center text-sm font-medium text-a-muted">Sign in with your email or mobile number.</p>
      </div>

      <div className="mb-4 flex rounded-xl border border-a-border bg-a-surface p-1">
        {["email", "mobile"].map((m) => {
          const active = mode === m;
          const Icon = m === "email" ? Mail : Phone;
          return (
            <button
              key={m}
              data-testid={`login-tab-${m}`}
              onClick={() => { setMode(m); setIdentifier(""); }}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2.5 text-[13px] font-bold transition-colors ${active ? "bg-a-primary text-white" : "text-a-text hover:bg-a-text/5"}`}
            >
              <Icon size={14} /> {m === "email" ? "Email" : "Mobile"}
            </button>
          );
        })}
      </div>

      <form className="flex flex-col gap-3.5" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        {mode === "email" ? (
          <Input testID="login-identifier-input" label="Email" type="email" autoComplete="email" placeholder="you@company.com"
            value={identifier} onChangeText={setIdentifier} leading={<Mail size={18} />} />
        ) : (
          <PhoneInput testID="login-identifier-input" label="Mobile number" placeholder="98765 43210"
            valueE164={identifier} onChangeE164={(e164) => setIdentifier(e164)} showValidationChecked={false} />
        )}
        <Input
          testID="login-password-input" label="Password" type={showPw ? "text" : "password"} autoComplete="current-password"
          placeholder="Your password" value={password} onChangeText={setPassword} onSubmitEditing={handleSubmit}
          leading={<Lock size={18} />}
          trailing={<button type="button" onClick={() => setShowPw((v) => !v)} className="text-a-muted hover:text-a-text" data-testid="login-toggle-password">{showPw ? <EyeOff size={18} /> : <Eye size={18} />}</button>}
        />
        <Button testID="login-submit-button" type="submit" label="Sign in" size="lg" loading={loading} className="mt-2" />

        <button
          type="button"
          data-testid="login-google-button"
          onClick={google}
          className="btn-press flex items-center justify-center gap-2 rounded-xl border border-a-border bg-a-surface py-3 font-bold text-a-text hover:border-a-primary/50"
        >
          <GoogleIcon /> Sign in with Google
        </button>

        <div className="mt-1 flex justify-between">
          <Link to="/forgot-password" className="text-[13px] font-bold text-a-primary hover:underline" data-testid="login-forgot-password">Forgot password?</Link>
          <Link to="/forgot-email" className="text-[13px] font-bold text-a-primary hover:underline" data-testid="login-forgot-email">Forgot email ID?</Link>
        </div>
        <div className="my-3 h-px bg-a-border" />
        <div className="flex justify-center gap-2">
          <span className="text-a-muted">New here?</span>
          <Link to="/register" replace className="font-bold text-a-primary hover:underline" data-testid="login-register-link">Create account</Link>
        </div>
        <div className="mt-3 text-center">
          <Link to="/privacy-policy" className="text-xs text-a-muted underline" data-testid="login-privacy-link">Privacy Policy</Link>
        </div>
      </form>
    </AuthBackground>
  );
}
